import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { theme } from "../../../theme";
import { Typography, Box, Autocomplete } from "@mui/material";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import { styled } from "@mui/material/styles";

const DiagnosticoGeneral = (props) => {
  const {
    data,
    diagnostico,
    setDiagnostico,
    handleOnChange,
    checked,
    title,
    estilos,
    onInputChange,
    particular,
    onError,
  } = props;
  const classes = useStyles(theme);
  const [error, setError] = React.useState(false);
  const [touched, setTouched] = React.useState(false);

  const handleInputChange = (event, value) => {
    setTouched(true);
    
    // Si no hay valor, marcar como error y limpiar
    if (!value) {
      setError(true);
      onError?.(true);
      onInputChange("");
      return;
    }

    // Remover espacios extra al inicio y final, pero mantener espacios entre palabras
    const trimmedValue = value.trim();
    
    // Verificar si está vacío después de quitar espacios
    if (trimmedValue === "") {
      setError(true);
      onError?.(true);
      onInputChange("");
      return;
    }

    // Si es un JSON, validarlo
    if (trimmedValue.startsWith('{')) {
      try {
        JSON.parse(trimmedValue);
        setError(false);
        onInputChange(value);
      } catch (e) {
        // Si no es JSON válido, tratar como texto normal
        setError(false);
        onInputChange(value);
      }
    } else {
      // Para texto normal, permitir cualquier texto con contenido válido
      setError(false);
      onError?.(false);
      onInputChange(value);
    }
  };

  return (
    <div className={classes.container}>
      {title && (
        <Typography
          sx={{
            m: 1,
            fontSize: "0.91rem",
            color: estilos.colorTextSecondary,
            fontWeight: "500",
          }}
        >
          {title}
        </Typography>
      )}
      <Box
        sx={{
          padding: "15px 15px 2px",
          display: "flex",
          justifyContent: "space-between",
          flexWrap: "wrap",
        }}
      >
        <Autocomplete
          id="diagnostico"
          value={diagnostico}
          freeSolo
          clearOnBlur={false}
          onChange={(event, newValue) => {
            // Si no hay valor, marcar error
            if (!newValue) {
              setError(true);
              setDiagnostico(null);
              return;
            }

            // Para texto ingresado manualmente
            if (typeof newValue === 'string') {
              const trimmedValue = newValue.trim();
              if (trimmedValue === '') {
                setError(true);
                setDiagnostico(null);
              } else {
                setError(false);
                // Mantener el texto ingresado como está
                setDiagnostico(newValue);
              }
              return;
            }

            // Para opciones seleccionadas del autocomplete (objetos)
            setError(false);
            setDiagnostico(newValue);
          }}
          onInputChange={(event, value) => handleInputChange(event, value)}
          sx={{ width: "69%" }}
          filterSelectedOptions
          getOptionLabel={(option) => (option && option.descDiagnostico) || ""}
          options={data}
          noOptionsText={"Sin resultados"}
          renderInput={(params) => (
            <CustomTextField
              {...params}
              InputLabelProps={{
                shrink: true,
              }}
              size="small"
              color={estilos.backgroundColorPrimary}
              fullWidth
              label="Diagnóstico"
              id="diagnostico"
              error={touched && error}
              helperText={
                touched && error ? "Debe ingresar un diagnóstico válido" : ""
              }
              onBlur={() => {
                setTouched(true);
                const value = params.inputProps.value;
                
                // Solo validamos si está vacío, pero mantenemos el valor
                const hasError = !value || value.trim() === "";
                setError(hasError);
                onError?.(hasError);
              }}
            />
          )}
        />
        <CustomTextField
          size="small"
          color={estilos.backgroundColorPrimary}
          sx={{ width: "29%" }}
          label="Código"
          disabled
          value={
            diagnostico && diagnostico.idDiagnostico !== 999999
              ? diagnostico.codDiagnostico
              : ""
          }
          InputProps={{
            classes: {
              input: classes.resizeFont,
            },
          }}
          InputLabelProps={{ style: { fontSize: 15 }, shrink: true }}
        />
      </Box>
      {!particular && (
        <Box
          sx={{
            padding: "2px 15px 5px",
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                size={"small"}
                sx={{
                  "&.Mui-checked": {
                    color: estilos.backgroundColorPrimary,
                  },
                }}
                checked={checked}
              />
            }
            onChange={() =>
              particular
                ? handleOnChange("imprimir-particular")
                : handleOnChange("imprimir")
            }
            label={
              <Typography
                sx={{ fontSize: "0.82rem", color: estilos.colorTextSecondary }}
              >
                No imprimir diagnóstico. Sólo imprimir código.
              </Typography>
            }
          />
        </Box>
      )}
    </div>
  );
};

export default DiagnosticoGeneral;

const useStyles = makeStyles((theme) => ({
  container: {
    flexDirection: "column",
    margin: "20px 0px",
  },
  resizeFont: {
    fontSize: 15,
  },
}));

const CustomTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== "color",
})(({ theme, color }) => ({
  "& label.Mui-focused": {
    color: color,
  },
  "& .MuiInput-underline:after": {
    borderBottomColor: color,
  },
  "& .MuiOutlinedInput-root": {
    "&:hover fieldset": {
      borderColor: color,
    },
    "&.Mui-focused fieldset": {
      borderColor: color,
    },
    fontSize: "15px",
  },
}));
